import { DateFormat } from '@agroone/dates'
import { UserDto } from '@agroone/entities'
import { HttpClient } from '@angular/common/http'
import { inject } from '@angular/core'
import { BehaviorSubject, map, Observable, retry } from 'rxjs'
import { UserServiceModel } from '../models'
import { requireDefined } from '../utils/require-defined.util'
import { LoggerService } from './logger.service'

export abstract class SharedUserService implements UserServiceModel {
  abstract userApiUrl: string

  protected readonly http = inject(HttpClient)
  protected readonly logger = inject(LoggerService)

  /** `null` until the user is loaded. */
  protected userSubject: BehaviorSubject<UserDto | null> = new BehaviorSubject<UserDto | null>(null)

  public user: Observable<UserDto | null> = this.userSubject.asObservable()
  public userCanUseNotificationFeature?: boolean

  public set currentUser(user: UserDto | null) {
    this.userSubject.next(user)
  }
  public get currentUser(): UserDto | null {
    return this.userSubject.getValue()
  }

  /** The loaded user, for code that only runs once the application initializer has loaded it. */
  public requireCurrentUser(): UserDto {
    return requireDefined(this.currentUser, 'the current user to be loaded')
  }

  /**
   * The user's date formats, `undefined` until the user is loaded: the `date` pipe then falls back to its
   * default format, as it would for `null`, and accepts the value as is.
   */
  private readonly _defaultDateUserSubject = new BehaviorSubject<DateFormat | undefined>(undefined)

  private readonly _defaultDateHourUserSubject = new BehaviorSubject<DateFormat | undefined>(undefined)

  public set currentUserDateFormat(format: DateFormat | undefined) {
    this._defaultDateUserSubject.next(format)
  }

  public get currentUserDateFormat(): DateFormat | undefined {
    return this._defaultDateUserSubject.getValue()
  }

  public set currentUserDateHourFormat(format: DateFormat | undefined) {
    this._defaultDateHourUserSubject.next(format)
  }

  public get currentUserDateHourFormat(): DateFormat | undefined {
    return this._defaultDateHourUserSubject.getValue()
  }

  public abstract init(): Observable<UserDto>

  protected getCurrentUser(): Observable<UserDto> {
    return this.http.get<UserDto>(`${this.userApiUrl}/me`).pipe(
      retry(1),
      map((user: UserDto) => {
        user.availableRegions.sort((x, y) => x.localeCompare(y))
        const newUser: UserDto = new UserDto(user)
        if (user) {
          this.userSubject.next(newUser)
          this.logger.log('User Initialized')
        }
        return newUser
      }),
    )
  }

  public getActiveUsersWithPhytoCertification(): Observable<UserDto[]> {
    return this.http.get<UserDto[]>(`${this.userApiUrl}/phyto-certification`)
  }
}
